import React, { useState, useEffect, useRef, useMemo } from "react";
import Map, {
  Marker,
  Source,
  Layer,
  LayerProps,
  ViewState,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { PopularTrip, Location } from "@/types/trip";
import { Home, ArrowLeft, ArrowRight, Minimize2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { formatPrice, getFlagImage } from "@/lib/general";
import currencyStore from "@/stores/ui/currencyStore";
import logger from "@/utils/logger";
import { useNavigate } from "react-router-dom";

interface TripOverviewMapProps {
  trip: PopularTrip | null;
  isLoading: boolean;
}

const generateTripDescription = (trip: PopularTrip): string => {
  if (!trip || !trip.locations || trip.locations.length === 0) {
    return "Explore this exciting trip with Waytora.";
  }

  const locationCount = trip.locations.length;
  const countries = [
    ...new Set(trip.locations.map((loc) => loc.address_components.country)),
  ];
  const firstLocation = trip.locations[0];
  const lastLocation = trip.locations[trip.locations.length - 1];

  // Calculate trip duration
  let tripDuration = 0;
  if (trip.locations.length > 0) {
    const startDate = new Date(
      Math.min(
        ...trip.locations.map((loc) =>
          new Date(loc.date?.start_date || 0).getTime()
        )
      )
    );
    const endDate = new Date(
      Math.max(
        ...trip.locations.map((loc) =>
          new Date(loc.date?.end_date || 0).getTime()
        )
      )
    );
    tripDuration = Math.max(
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1,
      0
    );
  }

  // Generate description based on available information
  if (countries.length === 1 && firstLocation) {
    return `A ${tripDuration}-day adventure exploring ${locationCount} beautiful destinations in ${countries[0]}, starting in ${firstLocation.name}.`;
  } else if (countries.length > 1 && firstLocation && lastLocation) {
    return `An exciting ${tripDuration}-day journey across ${countries.length} countries, visiting ${locationCount} destinations from ${firstLocation.name} to ${lastLocation.name}.`;
  } else {
    return `Discover this ${tripDuration}-day trip featuring ${locationCount} carefully selected destinations.`;
  }
};

const LocationBox: React.FC<{ location: Location | null }> = ({ location }) => {
  if (!location) return null;

  return (
    <div className="absolute top-2 right-2 bg-neutral-100/90 p-2 rounded-md shadow-md z-10 flex items-center">
      <img
        src={getFlagImage(location.address_components.country)}
        alt={`${location.address_components.country} flag`}
        className="w-6 h-6 mr-2 rounded-sm object-cover"
      />
      <span className="text-sm font-medium">{location.name}</span>
    </div>
  );
};

export const TripOverviewMap: React.FC<TripOverviewMapProps> = ({
  trip,
  isLoading,
}) => {
  const { selectedCurrency } = currencyStore();
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string;
  const [viewport, setViewport] = useState<ViewState>({
    longitude: trip?.locations?.[0]?.coordinates.longitude ?? 10,
    latitude: trip?.locations?.[0]?.coordinates.latitude ?? 50,
    zoom: trip ? 12 : 2.5,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null
  );
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [addCount, setAddCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (
      trip?.locations &&
      trip.locations.length > 0 &&
      mapRef.current &&
      isMapLoaded
    ) {
      const firstLocation = trip.locations[0];
      if (firstLocation) {
        mapRef.current.flyTo({
          center: [
            firstLocation.coordinates.longitude,
            firstLocation.coordinates.latitude,
          ],
          zoom: 12,
          duration: 1500,
          essential: true,
        });
        setSelectedLocation(firstLocation);
      }
    }
  }, [trip, isMapLoaded]);

  const routeLayer: LayerProps = {
    id: "route",
    type: "line",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": "#CBD5E1",
      "line-width": ["interpolate", ["linear"], ["zoom"], 8, 2, 12, 4],
      "line-opacity": 0.6,
      "line-blur": 0.5,
    },
  };

  const routeGlowLayer: LayerProps = {
    id: "route-glow",
    type: "line",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": "#CBD5E1",
      "line-width": ["interpolate", ["linear"], ["zoom"], 8, 4, 12, 8],
      "line-opacity": 0.2,
      "line-blur": 2,
    },
  };

  const getRouteData = () => {
    if (!trip?.locations || trip.locations.length === 0) return null;

    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: trip.locations.map((location) => [
          location.coordinates.longitude,
          location.coordinates.latitude,
        ]),
      },
    } as const;
  };

  const handleLocationClick = (location: Location) => {
    setSelectedLocation(location);
    if (mapRef.current) {
      setViewport({
        ...viewport,
        longitude: location.coordinates.longitude,
        latitude: location.coordinates.latitude,
        zoom: 10,
      });
    }
  };

  const handleZoomOut = () => {
    if (trip?.locations && trip.locations.length > 0 && mapRef.current) {
      const firstLocation = trip.locations[0];
      if (firstLocation) {
        mapRef.current.flyTo({
          center: [
            firstLocation.coordinates.longitude,
            firstLocation.coordinates.latitude,
          ],
          zoom: 2.5,
          duration: 1500,
        });
      }
    }
  };

  const handleNextLocation = () => {
    if (!selectedLocation || !trip?.locations) return;
    const currentIndex = trip.locations.findIndex(
      (loc) => loc.id === selectedLocation.id
    );
    const nextLocation = trip.locations[currentIndex + 1];
    if (currentIndex < trip.locations.length - 1 && nextLocation) {
      handleLocationClick(nextLocation);
    }
  };

  const handlePreviousLocation = () => {
    if (!selectedLocation || !trip?.locations) return;
    const currentIndex = trip.locations.findIndex(
      (loc) => loc.id === selectedLocation.id
    );
    const prevLocation = trip.locations[currentIndex - 1];
    if (currentIndex > 0 && prevLocation) {
      handleLocationClick(prevLocation);
    }
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked((prev) => !prev);
    setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1));
  };

  const handleAdd = () => {
    if (!isAdded) {
      setIsAdded(true);
      setAddCount((prev) => prev + 1);
      logger.log(`${trip?.name || "Unnamed Trip"} added to trips`);
    }
  };

  const handleShare = (newCount: number) => {
    // Add your logic for sharing the trip here
  };

  const handleOpenTrip = () => {
    if (!trip?.trip_id) return;
    logger.log("Opening trip:", trip.trip_id);
    navigate(`/trip-builder/${trip.trip_id}`);
  };

  const tripDetails = useMemo(() => {
    if (!trip?.locations || trip.locations.length === 0) {
      return {
        locationCount: 0,
        totalDays: 0,
        estimatedCost: 0,
        startDate: null,
        endDate: null,
      };
    }

    const locationCount = trip.locations.length;

    const startDate = new Date(
      Math.min(
        ...trip.locations.map((loc) =>
          new Date(loc.date?.start_date || 0).getTime()
        )
      )
    );
    const endDate = new Date(
      Math.max(
        ...trip.locations.map((loc) =>
          new Date(loc.date?.end_date || 0).getTime()
        )
      )
    );
    const totalDays = Math.max(
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1,
      0
    );

    const estimatedCost = Math.round(
      trip.locations.reduce(
        (sum, location) => sum + (location.budget?.total || 0),
        0
      )
    );

    return {
      locationCount,
      totalDays,
      estimatedCost,
      startDate,
      endDate,
    };
  }, [trip]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full w-[40%] py-2">
        {/* Map skeleton */}
        <div className="h-[66.666667%] relative rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50 animate-pulse"></div>

        {/* Trip details skeleton */}
        <div className="flex-1 mt-2 bg-white p-5 overflow-hidden rounded-xl border border-neutral-200 flex flex-col shadow-sm">
          <div className="flex-grow" style={{ minHeight: 0 }}>
            {/* Title skeleton */}
            <div className="flex justify-between items-start mb-2">
              <div className="h-6 bg-neutral-200 rounded-xl w-3/4 animate-pulse"></div>
              <div className="w-4 h-4 rounded-full bg-neutral-200 animate-pulse"></div>
            </div>

            {/* Description skeleton */}
            <div className="h-4 bg-neutral-200 rounded-xl w-full mt-1.5 mb-2 animate-pulse"></div>
            <div className="h-4 bg-neutral-200 rounded-xl w-4/5 mb-4 animate-pulse"></div>

            {/* Trip stats skeleton */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-indigo-50/50 px-3 h-8 flex items-center justify-center rounded-lg ring-1 ring-indigo-200">
                <div className="h-3 bg-indigo-200/70 rounded-xl w-16 animate-pulse"></div>
              </div>
              <div className="bg-indigo-50/50 px-3 h-8 flex items-center justify-center rounded-lg ring-1 ring-indigo-200">
                <div className="h-3 bg-indigo-200/70 rounded-xl w-16 animate-pulse"></div>
              </div>
              <div className="bg-indigo-50/50 px-3 h-8 flex items-center justify-center rounded-lg ring-1 ring-indigo-200">
                <div className="h-3 bg-indigo-200/70 rounded-xl w-16 animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Button skeleton */}
          <div className="flex-shrink-0">
            <div className="w-full bg-indigo-200 rounded-full h-10 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!trip?.locations || trip.locations.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col h-full w-[40%] py-2">
      <div className="h-[66.666667%] 2xl:h-[70%] relative rounded-xl overflow-hidden border border-neutral-200">
        {trip && (
          <>
            <AnimatePresence>
              {selectedLocation && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-3 top-3 z-10 bg-white backdrop-blur-sm p-2 rounded-full shadow-sm border border-neutral-200/50"
                >
                  <div className="flex items-center gap-2">
                    <img
                      src={getFlagImage(
                        selectedLocation.address_components.country
                      )}
                      alt={`${selectedLocation.address_components.country} flag`}
                      className="w-4 h-4 rounded-sm object-cover"
                    />
                    <span className="text-xs font-normal text-neutral-600">
                      {selectedLocation.name}
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            {trip.locations && trip.locations.length > 1 && (
              <div className="absolute bottom-2 left-2 z-10 flex gap-1.5">
                <button
                  onClick={handlePreviousLocation}
                  disabled={
                    !selectedLocation ||
                    trip.locations.findIndex(
                      (loc) => loc.id === selectedLocation.id
                    ) === 0
                  }
                  className="bg-white backdrop-blur-sm w-8 h-8 rounded-full shadow-sm hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-default border border-neutral-100 flex items-center justify-center"
                  title="Previous location"
                >
                  <ArrowLeft className="w-4 h-4 text-neutral-500" />
                </button>
                <button
                  onClick={handleNextLocation}
                  disabled={
                    !selectedLocation ||
                    trip.locations.findIndex(
                      (loc) => loc.id === selectedLocation.id
                    ) ===
                      trip.locations.length - 1
                  }
                  className="bg-white backdrop-blur-sm w-8 h-8 rounded-full shadow-sm hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-default border border-neutral-100 flex items-center justify-center"
                  title="Next location"
                >
                  <ArrowRight className="w-4 h-4 text-neutral-500" />
                </button>
              </div>
            )}

            {/* Zoom Out Button */}
            <button
              onClick={handleZoomOut}
              className="absolute bottom-2 right-2 z-10 bg-white backdrop-blur-sm w-8 h-8 rounded-full shadow-sm hover:bg-white transition-colors border border-neutral-100 flex items-center justify-center"
              title="Zoom out to see all locations"
            >
              <Minimize2 className="w-4 h-4 text-neutral-500" />
            </button>
          </>
        )}

        <Map
          reuseMaps
          mapboxAccessToken={mapboxToken}
          ref={(ref) => {
            if (ref) mapRef.current = ref.getMap();
          }}
          onLoad={() => setIsMapLoaded(true)}
          {...viewport}
          minZoom={2.5}
          onMove={(evt) => setViewport(evt.viewState)}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/jamiedennis8/clxmyy67600fs01pu0d6q2ha7/draft"
          attributionControl={false}
        >
          {trip?.locations?.map((location, index) => (
            <Marker
              key={location.id}
              longitude={location.coordinates.longitude}
              latitude={location.coordinates.latitude}
            >
              <div
                className={`w-7 h-7 ${
                  index === 0
                    ? "bg-white text-neutral-600 border border-neutral-300"
                    : "bg-white text-neutral-600 border border-neutral-300"
                } rounded-full flex items-center justify-center shadow-sm cursor-pointer transition-all duration-200 hover:border-neutral-400 hover:shadow-md`}
                onClick={() => handleLocationClick(location)}
              >
                {index === 0 ? (
                  <Home className="w-3.5 h-3.5" />
                ) : (
                  <span className="text-xs font-medium">{index}</span>
                )}
              </div>
            </Marker>
          ))}
          {trip && getRouteData() && (
            <Source type="geojson" data={getRouteData()!}>
              <Layer {...routeGlowLayer} />
              <Layer {...routeLayer} />
            </Source>
          )}
        </Map>
      </div>

      {trip ? (
        <div className="flex-1 mt-2 bg-white 2xl:p-5 p-3 overflow-hidden rounded-xl border border-neutral-200 flex flex-col shadow-sm">
          <div className="flex-grow" style={{ minHeight: 0 }}>
            <div className="flex justify-between items-start mb-2 2xl:mb-4">
              <h3 className="font-semibold text-[16px] text-neutral-800 line-clamp-1 flex-1">
                {trip.name || "Unnamed Trip"}
              </h3>
              {trip.locations?.[0]?.address_components?.country && (
                <div className="flex items-center gap-1 2xl:-translate-y-1.5">
                  <img
                    src={getFlagImage(
                      trip.locations[0].address_components.country
                    )}
                    alt={`${trip.locations[0].address_components.country} flag`}
                    className="w-5 h-5 rounded-sm object-cover 2xl:w-7 2xl:h-7"
                  />
                </div>
              )}
            </div>

            {trip.description ? (
              <p className="text-sm text-neutral-600 font-light mt-1.5 mb-4 line-clamp-2">
                {trip.description}
              </p>
            ) : (
              <p className="text-sm text-neutral-600 font-light mt-1.5 mb-4 line-clamp-2">
                {generateTripDescription(trip)}
              </p>
            )}

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-indigo-50/50 px-3 h-8 flex items-center justify-center rounded-lg ring-1 ring-indigo-200">
                <span className="text-xs font-normal text-indigo-800 2xl:flex-col 2xl:items-start 2xl:justify-start 2xl:flex">
                  {tripDetails.locationCount}{" "}
                  {tripDetails.locationCount === 1 ? "Location" : "Locations"}
                </span>
              </div>
              <div className="bg-indigo-50/50 px-3 h-8 flex items-center justify-center rounded-lg ring-1 ring-indigo-200">
                <span className="text-xs font-normal text-indigo-800 2xl:flex-col 2xl:items-start 2xl:justify-start 2xl:flex">
                  Est. Cost{" "}
                  {formatPrice(
                    selectedCurrency.code,
                    tripDetails.estimatedCost
                  )}
                </span>
              </div>
              <div className="bg-indigo-50/50 px-3 h-8 flex items-center justify-center rounded-lg ring-1 ring-indigo-200">
                <span className="text-xs font-normal text-indigo-800 2xl:flex-col 2xl:items-start 2xl:justify-start 2xl:flex">
                  {tripDetails.totalDays === 1
                    ? "1 Day"
                    : `${tripDetails.totalDays} Days`}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0">
            <button
              onClick={handleOpenTrip}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-8 2xl:h-10 text-sm font-medium transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow"
            >
              <span>Open Trip</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 ml-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 mt-2 bg-white p-5 overflow-hidden rounded-xl border border-neutral-200 flex flex-col justify-center items-center shadow-sm">
          <div className="w-14 h-14 bg-neutral-50 rounded-full flex items-center justify-center mb-4 border border-neutral-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
          </div>
          <p className="text-[15px] font-medium text-neutral-700 text-center">
            Select a trip to get started
          </p>
          <p className="text-sm text-neutral-500 text-center mt-1.5 font-light">
            Choose a trip from the list to view its details and map
          </p>
        </div>
      )}
    </div>
  );
};
